import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
    TrendingUp, TrendingDown, DollarSign, Package, Users, 
    Calendar, Target, Award, Clock, CheckCircle, AlertCircle,
    BarChart3, PieChart, Activity, ArrowUpRight, ArrowDownRight,
    Download, FileSpreadsheet, FileText
} from 'lucide-react';
import { format, subDays, subMonths, parseISO } from 'date-fns';
import { fetchQuotesForOrders, subscribeToCacheUpdates } from '../utils/quotesService';

const AnalyticsDashboard = ({ allOrders, allVendors, currentUser }) => {
    const [selectedPeriod, setSelectedPeriod] = useState('30d');
    const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
    const periodDropdownRef = useRef(null);
    const [quotesData, setQuotesData] = useState({});
    const [isLoadingQuotes, setIsLoadingQuotes] = useState(true);
    const [loadedQuotesCount, setLoadedQuotesCount] = useState(0);
    const [totalQuotesToLoad, setTotalQuotesToLoad] = useState(0);
    const [showExportModal, setShowExportModal] = useState(false);

    // Click outside handler for dropdown
    const useClickOutside = (ref, callback) => {
        useEffect(() => {
            const handleClickOutside = (event) => {
                if (ref.current && !ref.current.contains(event.target)) {
                    callback();
                }
            };
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }, [ref, callback]);
    };

    useClickOutside(periodDropdownRef, () => setShowPeriodDropdown(false));

    // Period options
    const periodOptions = [
        { label: 'Last 7 days', value: '7d' },
        { label: 'Last 30 days', value: '30d' },
        { label: 'Last 90 days', value: '90d' },
        { label: 'Last 6 months', value: '6m' }
    ];

    const getCurrentPeriodLabel = () => {
        const option = periodOptions.find(opt => opt.value === selectedPeriod);
        return option ? option.label : 'Last 30 days';
    };

    // Optimized quotes fetching using shared service
    const fetchQuotesData = useCallback(async () => {
        if (allOrders.length === 0) {
            setIsLoadingQuotes(false);
            return;
        }

        setIsLoadingQuotes(true);
        setLoadedQuotesCount(0);
        
        try {
            const quotesMap = await fetchQuotesForOrders(allOrders, (loaded, total) => {
                setLoadedQuotesCount(loaded);
                setTotalQuotesToLoad(total);
            });
            
            setQuotesData(quotesMap);
        } catch (error) {
            console.error('Error fetching quotes:', error);
        } finally {
            setIsLoadingQuotes(false);
        }
    }, [allOrders]);

    // Subscribe to cache updates
    useEffect(() => {
        const unsubscribe = subscribeToCacheUpdates((orderId, quotes) => {
            setQuotesData(prev => ({
                ...prev,
                [orderId]: quotes
            }));
        });

        return unsubscribe;
    }, []);

    // Fetch quotes data on mount and when orders change
    useEffect(() => {
        fetchQuotesData();
    }, [fetchQuotesData]);

    const dashboardData = useMemo(() => {
        if (!allOrders || !allVendors) return {};

        const now = new Date();
        let startDate;
        
        switch (selectedPeriod) {
            case '7d':
                startDate = subDays(now, 7);
                break;
            case '30d':
                startDate = subDays(now, 30);
                break;
            case '90d':
                startDate = subDays(now, 90);
                break;
            case '6m':
                startDate = subMonths(now, 6);
                break;
            default:
                startDate = subDays(now, 30);
        }

        // Filter orders by date range
        const filteredOrders = allOrders.filter(order => {
            const orderDate = order.createdAt?.toDate?.() || new Date(order.createdAt);
            return orderDate >= startDate && orderDate <= now;
        });

        // Calculate key metrics - for placed orders (In Progress, Completed)
        const placedOrders = filteredOrders.filter(order => 
            order.status === 'In Progress' || 
            order.status === 'Completed'
        );
        
        const totalRevenue = placedOrders.reduce((sum, order) => {
            const orderQuotes = quotesData[order.id] || [];
            return sum + orderQuotes.reduce((quoteSum, quote) => 
                quoteSum + (parseFloat(quote.price) || 0), 0);
        }, 0);

        const totalOrders = filteredOrders.length;
        const placedOrderCount = placedOrders.length;
        const completedOrders = filteredOrders.filter(order => order.status === 'Completed').length;
        const pendingOrders = filteredOrders.filter(order => 
            order.status === 'Awaiting Requirements' || order.status === 'Negotiation'
        ).length;

        const avgOrderValue = placedOrderCount > 0 ? totalRevenue / placedOrderCount : 0;
        const completionRate = totalOrders > 0 ? (placedOrderCount / totalOrders) * 100 : 0;

        // Calculate growth rates (compare with previous period)
        const previousStartDate = new Date(startDate);
        const previousEndDate = new Date(startDate);
        const periodLength = (now - startDate) / (1000 * 60 * 60 * 24);
        previousStartDate.setDate(previousStartDate.getDate() - periodLength);
        previousEndDate.setDate(previousEndDate.getDate() - 1);

        const previousOrders = allOrders.filter(order => {
            const orderDate = order.createdAt?.toDate?.() || new Date(order.createdAt);
            return orderDate >= previousStartDate && orderDate <= previousEndDate;
        });

        const previousPlacedOrders = previousOrders.filter(order => 
            order.status === 'In Progress' || 
            order.status === 'Completed'
        );
        const previousRevenue = previousPlacedOrders.reduce((sum, order) => {
            const orderQuotes = quotesData[order.id] || [];
            return sum + orderQuotes.reduce((quoteSum, quote) => 
                quoteSum + (parseFloat(quote.price) || 0), 0);
        }, 0);

        const revenueGrowth = previousRevenue > 0 ? ((totalRevenue - previousRevenue) / previousRevenue) * 100 : 0;
        const orderGrowth = previousPlacedOrders.length > 0 ? ((placedOrderCount - previousPlacedOrders.length) / previousPlacedOrders.length) * 100 : 0;

        // Top performing vendors - for placed orders
        const vendorPerformance = {};
        allVendors.forEach(vendor => {
            const vendorOrders = filteredOrders.filter(order => {
                if (order.status !== 'In Progress' && order.status !== 'Completed') return false;
                const orderQuotes = quotesData[order.id] || [];
                return orderQuotes.some(quote => quote.supplier === vendor.name);
            });
            
            const vendorRevenue = vendorOrders.reduce((sum, order) => {
                const orderQuotes = quotesData[order.id] || [];
                return sum + orderQuotes.filter(q => q.supplier === vendor.name)
                    .reduce((quoteSum, quote) => quoteSum + (parseFloat(quote.price) || 0), 0);
            }, 0);

            vendorPerformance[vendor.name] = {
                name: vendor.name,
                orders: vendorOrders.length,
                revenue: vendorRevenue
            };
        });

        const topVendors = Object.values(vendorPerformance)
            .sort((a, b) => b.revenue - a.revenue)
            .slice(0, 5);

        // Recent activity
        const recentOrders = filteredOrders
            .sort((a, b) => {
                const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
                const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
                return dateB - dateA;
            })
            .slice(0, 5);

        // Status distribution
        const statusCounts = filteredOrders.reduce((acc, order) => {
            acc[order.status] = (acc[order.status] || 0) + 1;
            return acc;
        }, {});

        return {
            metrics: {
                totalRevenue,
                totalOrders,
                placedOrderCount,
                completedOrders,
                pendingOrders,
                avgOrderValue,
                completionRate,
                revenueGrowth,
                orderGrowth
            },
            topVendors,
            recentOrders,
            statusCounts
        };
    }, [allOrders, allVendors, selectedPeriod, quotesData]);

    const renderMetricCard = (title, value, icon, growth, subtitle, color = 'blue') => {
        const colorClasses = {
            blue: 'bg-blue-50 text-blue-600',
            green: 'bg-green-50 text-green-600',
            red: 'bg-red-50 text-red-600',
            yellow: 'bg-yellow-50 text-yellow-600',
            purple: 'bg-purple-50 text-purple-600'
        };

        return (
            <div className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-gray-600">{title}</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">
                            {typeof value === 'number' && title.toLowerCase().includes('revenue') 
                                ? `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                                : typeof value === 'number' && title.toLowerCase().includes('rate')
                                ? `${value.toFixed(1)}%`
                                : typeof value === 'number'
                                ? value.toLocaleString('en-IN')
                                : value
                            }
                        </p>
                        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
                    </div>
                    <div className={`p-3 rounded-full ${colorClasses[color]}`}>
                        {React.createElement(icon, { size: 24 })}
                    </div>
                </div>
                {growth !== undefined && (
                    <div className="flex items-center mt-4">
                        {React.createElement(growth >= 0 ? TrendingUp : TrendingDown, { 
                            size: 16, 
                            className: growth >= 0 ? 'text-green-500' : 'text-red-500' 
                        })}
                        <span className={`text-sm font-medium ml-1 ${growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {Math.abs(growth).toFixed(1)}%
                        </span>
                        <span className="text-sm text-gray-500 ml-1">vs previous period</span>
                    </div>
                )}
            </div>
        );
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return 'text-green-600 bg-green-100';
            case 'inProgress':
                return 'text-blue-600 bg-blue-100';
            case 'awaitingRequirements':
                return 'text-yellow-600 bg-yellow-100';
            case 'negotiation':
                return 'text-purple-600 bg-purple-100';
            default:
                return 'text-gray-600 bg-gray-100';
        }
    };

    if (isLoadingQuotes) {
        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
                        <p className="text-gray-600">Quick overview of your business performance</p>
                    </div>
                    <div className="relative" ref={periodDropdownRef}>
                        <button 
                            onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                            className="px-4 py-2 text-sm font-medium bg-transparent border-none outline-none transition-colors interactive-button text-black hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled
                        >
                            {getCurrentPeriodLabel()}
                        </button>
                        {showPeriodDropdown && (
                            <div className="absolute right-0 mt-2 w-48 bg-white border rounded-md shadow-lg z-20">
                                {periodOptions.map(option => (
                                    <button
                                        key={option.value}
                                        onClick={() => {
                                            setSelectedPeriod(option.value);
                                            setShowPeriodDropdown(false);
                                        }}
                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${option.value === selectedPeriod ? 'bg-gray-50 font-semibold' : ''}`}
                                        disabled
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Loading Progress */}
                <div className="bg-white rounded-lg shadow-sm border p-6">
                    <div className="flex items-center justify-center">
                        <div className="text-center">
                            <Activity className="mx-auto h-12 w-12 text-gray-400 animate-spin" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">Loading analytics data...</h3>
                            <p className="text-sm text-gray-500 mt-1">
                                Loading quotes for {loadedQuotesCount} of {totalQuotesToLoad} orders
                            </p>
                            {totalQuotesToLoad > 0 && (
                                <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
                                    <div 
                                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${(loadedQuotesCount / totalQuotesToLoad) * 100}%` }}
                                    ></div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Show basic metrics while loading */}
                {dashboardData.metrics && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {renderMetricCard(
                            'Total Orders',
                            dashboardData.metrics.totalOrders,
                            Package,
                            undefined,
                            undefined,
                            'blue'
                        )}
                        {renderMetricCard(
                            'Placed Orders',
                            dashboardData.metrics.placedOrderCount,
                            CheckCircle,
                            undefined,
                            undefined,
                            'green'
                        )}
                        {renderMetricCard(
                            'Completed Orders',
                            dashboardData.metrics.completedOrders,
                            Award,
                            undefined,
                            undefined,
                            'green'
                        )}
                        {renderMetricCard(
                            'Completion Rate',
                            dashboardData.metrics.completionRate,
                            Target,
                            undefined,
                            undefined,
                            'purple'
                        )}
                    </div>
                )}
            </div>
        );
    }

    if (!dashboardData.metrics) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Activity className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No data available</h3>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
                    <p className="text-gray-600">Quick overview of your business performance</p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="relative" ref={periodDropdownRef}>
                        <button 
                            onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                            className="px-4 py-2 text-sm font-medium bg-transparent border-none outline-none transition-colors interactive-button text-black hover:text-gray-700"
                        >
                            {getCurrentPeriodLabel()}
                        </button>
                        {showPeriodDropdown && (
                            <div className="absolute right-0 mt-2 w-48 bg-white border rounded-md shadow-lg z-20">
                                {periodOptions.map(option => (
                                    <button
                                        key={option.value}
                                        onClick={() => {
                                            setSelectedPeriod(option.value);
                                            setShowPeriodDropdown(false);
                                        }}
                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${option.value === selectedPeriod ? 'bg-gray-50 font-semibold' : ''}`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    <button
                        onClick={() => setShowExportModal(true)}
                        className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-colors"
                        title="Generate Analytics Data"
                    >
                        <Download size={18} />
                        Generate Data
                    </button>
                </div>
            </div>

            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {renderMetricCard(
                    'Total Revenue (Placed Orders)',
                    dashboardData.metrics.totalRevenue,
                    DollarSign,
                    dashboardData.metrics.revenueGrowth,
                    undefined,
                    'green'
                )}
                {renderMetricCard(
                    'Placed Orders',
                    dashboardData.metrics.placedOrderCount,
                    Package,
                    dashboardData.metrics.orderGrowth,
                    undefined,
                    'blue'
                )}
                {renderMetricCard(
                    'Average Order Value (Placed Orders)',
                    dashboardData.metrics.avgOrderValue,
                    Target,
                    undefined,
                    'Per placed order',
                    'purple'
                )}
                {renderMetricCard(
                    'Completion Rate',
                    dashboardData.metrics.completionRate,
                    CheckCircle,
                    undefined,
                    undefined,
                    'green'
                )}
            </div>

            {/* Additional Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {renderMetricCard(
                    'Completed Orders',
                    dashboardData.metrics.completedOrders,
                    CheckCircle,
                    undefined,
                    undefined,
                    'green'
                )}
                {renderMetricCard(
                    'Pending Orders',
                    dashboardData.metrics.pendingOrders,
                    Clock,
                    undefined,
                    undefined,
                    'yellow'
                )}
                {renderMetricCard(
                    'Active Vendors',
                    dashboardData.topVendors.length,
                    Users,
                    undefined,
                    undefined,
                    'blue'
                )}
            </div>

            {/* Top Vendors and Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Performing Vendors */}
                <div className="bg-white rounded-lg shadow-sm border p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Vendors</h3>
                    <div className="space-y-4">
                        {dashboardData.topVendors.map((vendor, index) => (
                            <div key={vendor.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                        <span className="text-sm font-medium text-blue-600">{index + 1}</span>
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900">{vendor.name}</p>
                                        <p className="text-sm text-gray-500">{vendor.orders} orders</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="font-semibold text-gray-900">
                                        ₹{vendor.revenue.toLocaleString('en-IN')}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white rounded-lg shadow-sm border p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Orders</h3>
                    <div className="space-y-4">
                        {dashboardData.recentOrders.map((order) => (
                            <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                        <Package size={16} className="text-gray-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900 truncate max-w-32">
                                            {order.description || `Order #${order.serialNumber}`}
                                        </p>
                                        <p className="text-sm text-gray-500">
                                            {format(order.createdAt?.toDate?.() || new Date(order.createdAt), 'MMM dd, yyyy')}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                                        {order.status}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Status Distribution */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Status Distribution</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {Object.entries(dashboardData.statusCounts).map(([status, count]) => (
                        <div key={status} className="text-center p-4 bg-gray-50 rounded-lg">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2 ${getStatusColor(status)}`}>
                                {status === 'completed' && <CheckCircle size={20} />}
                                {status === 'inProgress' && <Activity size={20} />}
                                {status === 'awaitingRequirements' && <Clock size={20} />}
                                {status === 'negotiation' && <AlertCircle size={20} />}
                                {!['completed', 'inProgress', 'awaitingRequirements', 'negotiation'].includes(status) && <Package size={20} />}
                            </div>
                            <p className="text-2xl font-bold text-gray-900">{count}</p>
                            <p className="text-sm text-gray-500 capitalize">{status.replace(/([A-Z])/g, ' $1').trim()}</p>
                        </div>
                    ))}
                </div>
            </div>
            
            {/* Export Modal */}
            {showExportModal && (
                <ExportModal
                    dashboardData={dashboardData}
                    selectedPeriod={selectedPeriod}
                    onClose={() => setShowExportModal(false)}
                />
            )}
        </div>
    );
};

// Export Modal Component
const ExportModal = ({ dashboardData, selectedPeriod, onClose }) => {
    const [exportFormat, setExportFormat] = useState('excel');
    const [isExporting, setIsExporting] = useState(false);

    const handleExport = async () => {
        setIsExporting(true);
        try {
            if (exportFormat === 'excel') {
                await exportToExcel(dashboardData, selectedPeriod);
            } else if (exportFormat === 'pdf') {
                await exportToPDF(dashboardData, selectedPeriod);
            } else {
                await exportToText(dashboardData, selectedPeriod);
            }
            onClose();
        } catch (error) {
            console.error('Error exporting data:', error);
            alert('Failed to export data. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    const exportToExcel = async (data, period) => {
        try {
            const XLSX = await import('xlsx');
            
            const workbook = XLSX.utils.book_new();
            
            // Sheet 1: Dashboard Overview
            const overviewData = [
                ['Analytics Dashboard Overview'],
                [''],
                ['Period:', getPeriodLabel(period)],
                ['Report Generated:', new Date().toLocaleDateString('en-GB')],
                [''],
                ['Key Metrics'],
                ['Total Revenue (Placed Orders)', data.metrics?.totalRevenue || 'N/A'],
                ['Placed Orders', data.metrics?.placedOrderCount || 'N/A'],
                ['Completed Orders', data.metrics?.completedOrders || 'N/A'],
                ['Completion Rate', data.metrics?.completionRate || 'N/A'],
                ['Average Order Value', data.metrics?.avgOrderValue || 'N/A'],
                ['Pending Orders', data.metrics?.pendingOrders || 'N/A']
            ];
            
            const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
            overviewSheet['!cols'] = [{ width: 30 }, { width: 20 }];
            XLSX.utils.book_append_sheet(workbook, overviewSheet, 'Dashboard Overview');
            
            // Sheet 2: Top Vendors
            if (data.topVendors && data.topVendors.length > 0) {
                const vendorData = [
                    ['Top Performing Vendors'],
                    [''],
                    ['Rank', 'Vendor Name', 'Orders', 'Revenue (₹)']
                ];
                
                data.topVendors.forEach((vendor, index) => {
                    vendorData.push([
                        index + 1,
                        vendor.name,
                        vendor.orders,
                        vendor.revenue
                    ]);
                });
                
                const vendorSheet = XLSX.utils.aoa_to_sheet(vendorData);
                vendorSheet['!cols'] = [{ width: 10 }, { width: 30 }, { width: 15 }, { width: 20 }];
                XLSX.utils.book_append_sheet(workbook, vendorSheet, 'Top Vendors');
            }
            
            // Sheet 3: Recent Orders
            if (data.recentOrders && data.recentOrders.length > 0) {
                const orderData = [
                    ['Recent Orders'],
                    [''],
                    ['Order ID', 'Description', 'Status', 'Created Date']
                ];
                
                data.recentOrders.forEach(order => {
                    orderData.push([
                        order.serialNumber || order.id,
                        order.description || 'N/A',
                        order.status,
                        order.createdAt ? new Date(order.createdAt.toDate()).toLocaleDateString('en-GB') : 'N/A'
                    ]);
                });
                
                const orderSheet = XLSX.utils.aoa_to_sheet(orderData);
                orderSheet['!cols'] = [{ width: 20 }, { width: 40 }, { width: 20 }, { width: 20 }];
                XLSX.utils.book_append_sheet(workbook, orderSheet, 'Recent Orders');
            }
            
            // Sheet 4: Status Distribution
            if (data.statusCounts) {
                const statusData = [
                    ['Order Status Distribution'],
                    [''],
                    ['Status', 'Count']
                ];
                
                Object.entries(data.statusCounts).forEach(([status, count]) => {
                    statusData.push([status, count]);
                });
                
                const statusSheet = XLSX.utils.aoa_to_sheet(statusData);
                statusSheet['!cols'] = [{ width: 30 }, { width: 15 }];
                XLSX.utils.book_append_sheet(workbook, statusSheet, 'Status Distribution');
            }
            
            XLSX.writeFile(workbook, `Analytics_Dashboard_${period}_${new Date().toISOString().split('T')[0]}.xlsx`);
            
        } catch (error) {
            console.error('Error generating Excel:', error);
            throw error;
        }
    };

    const exportToPDF = async (data, period) => {
        try {
            const jsPDF = (await import('jspdf')).default;
            const autoTable = (await import('jspdf-autotable')).default;
            
            const doc = new jsPDF();
            
            // Set document properties
            doc.setProperties({
                title: `Analytics Dashboard - ${getPeriodLabel(period)}`,
                subject: 'Business Performance Analytics Report',
                author: 'UrbanTech Order Management',
                creator: 'UrbanTech System'
            });
            
            // Page 1: Title and Overview
            doc.setFontSize(24);
            doc.setTextColor(0, 0, 0);
            doc.text('Analytics Dashboard', 105, 30, { align: 'center' });
            
            doc.setFontSize(16);
            doc.setTextColor(100, 100, 100);
            doc.text(getPeriodLabel(period), 105, 50, { align: 'center' });
            
            // Key Metrics
            doc.setFontSize(14);
            doc.setTextColor(0, 0, 0);
            doc.text('Key Metrics', 20, 80);
            
            const metricsData = [
                ['Metric', 'Value'],
                ['Total Revenue (₹)', data.metrics?.totalRevenue || 'N/A'],
                ['Placed Orders', data.metrics?.placedOrderCount || 'N/A'],
                ['Completed Orders', data.metrics?.completedOrders || 'N/A'],
                ['Completion Rate', data.metrics?.completionRate || 'N/A'],
                ['Average Order Value (₹)', data.metrics?.avgOrderValue || 'N/A'],
                ['Pending Orders', data.metrics?.pendingOrders || 'N/A']
            ];
            
            autoTable(doc, {
                startY: 90,
                head: [['Metric', 'Value']],
                body: metricsData.slice(1),
                theme: 'grid',
                styles: {
                    fontSize: 12,
                    cellPadding: 5
                },
                headStyles: {
                    fillColor: [70, 130, 180],
                    textColor: 255,
                    fontStyle: 'bold'
                }
            });
            
            // Top Vendors
            if (data.topVendors && data.topVendors.length > 0) {
                doc.setFontSize(14);
                doc.text('Top Performing Vendors', 20, 160);
                
                const vendorData = data.topVendors.map((vendor, index) => [
                    index + 1,
                    vendor.name,
                    vendor.orders,
                    `₹${vendor.revenue.toLocaleString()}`
                ]);
                
                autoTable(doc, {
                    startY: 170,
                    head: [['Rank', 'Vendor Name', 'Orders', 'Revenue (₹)']],
                    body: vendorData,
                    theme: 'grid',
                    styles: {
                        fontSize: 11,
                        cellPadding: 4
                    },
                    headStyles: {
                        fillColor: [70, 130, 180],
                        textColor: 255,
                        fontStyle: 'bold'
                    }
                });
            }
            
            // Add new page for additional data
            if (data.recentOrders && data.recentOrders.length > 0) {
                doc.addPage();
                
                doc.setFontSize(16);
                doc.text('Recent Orders', 20, 30);
                
                const orderData = data.recentOrders.map(order => [
                    order.serialNumber || order.id,
                    order.description || 'N/A',
                    order.status,
                    order.createdAt ? new Date(order.createdAt.toDate()).toLocaleDateString('en-GB') : 'N/A'
                ]);
                
                autoTable(doc, {
                    startY: 40,
                    head: [['Order ID', 'Description', 'Status', 'Created Date']],
                    body: orderData,
                    theme: 'grid',
                    styles: {
                        fontSize: 9,
                        cellPadding: 3
                    },
                    headStyles: {
                        fillColor: [70, 130, 180],
                        textColor: 255,
                        fontStyle: 'bold'
                    }
                });
            }
            
            doc.save(`Analytics_Dashboard_${period}_${new Date().toISOString().split('T')[0]}.pdf`);
            
        } catch (error) {
            console.error('Error generating PDF:', error);
            throw error;
        }
    };

    const exportToText = async (data, period) => {
        const lines = [];
        
        lines.push('ANALYTICS DASHBOARD REPORT');
        lines.push('='.repeat(50));
        lines.push('');
        lines.push(`Period: ${getPeriodLabel(period)}`);
        lines.push(`Report Generated: ${new Date().toLocaleDateString('en-GB')}`);
        lines.push('');
        
        // Key Metrics
        lines.push('KEY METRICS');
        lines.push('-'.repeat(20));
        lines.push(`Total Revenue (₹): ${data.metrics?.totalRevenue || 'N/A'}`);
        lines.push(`Placed Orders: ${data.metrics?.placedOrderCount || 'N/A'}`);
        lines.push(`Completed Orders: ${data.metrics?.completedOrders || 'N/A'}`);
        lines.push(`Completion Rate: ${data.metrics?.completionRate || 'N/A'}`);
        lines.push(`Average Order Value (₹): ${data.metrics?.avgOrderValue || 'N/A'}`);
        lines.push(`Pending Orders: ${data.metrics?.pendingOrders || 'N/A'}`);
        lines.push('');
        
        // Top Vendors
        if (data.topVendors && data.topVendors.length > 0) {
            lines.push('TOP PERFORMING VENDORS');
            lines.push('-'.repeat(25));
            data.topVendors.forEach((vendor, index) => {
                lines.push(`${index + 1}. ${vendor.name} - ${vendor.orders} orders - ₹${vendor.revenue.toLocaleString()}`);
            });
            lines.push('');
        }
        
        // Recent Orders
        if (data.recentOrders && data.recentOrders.length > 0) {
            lines.push('RECENT ORDERS');
            lines.push('-'.repeat(15));
            data.recentOrders.forEach(order => {
                lines.push(`${order.serialNumber || order.id}: ${order.description || 'N/A'} - ${order.status}`);
            });
            lines.push('');
        }
        
        // Status Distribution
        if (data.statusCounts) {
            lines.push('ORDER STATUS DISTRIBUTION');
            lines.push('-'.repeat(25));
            Object.entries(data.statusCounts).forEach(([status, count]) => {
                lines.push(`${status}: ${count}`);
            });
        }
        
        const content = lines.join('\n');
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Analytics_Dashboard_${period}_${new Date().toISOString().split('T')[0]}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const getPeriodLabel = (period) => {
        const periodMap = {
            '7d': 'Last 7 days',
            '30d': 'Last 30 days',
            '90d': 'Last 90 days',
            '6m': 'Last 6 months'
        };
        return periodMap[period] || period;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md m-4">
                <h2 className="text-xl font-bold mb-4">Generate Analytics Data</h2>
                <p className="text-gray-600 mb-4">Select the format for your data:</p>
                <p className="text-sm text-blue-600 mb-4">All analytics data will be included in the generated file</p>
                
                <div className="space-y-3 mb-6">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="radio"
                            name="exportFormat"
                            value="excel"
                            checked={exportFormat === 'excel'}
                            onChange={(e) => setExportFormat(e.target.value)}
                            className="text-blue-600"
                        />
                        <div className="flex items-center gap-2">
                            <FileSpreadsheet size={20} className="text-green-600" />
                            <span>Excel Sheet (.xlsx)</span>
                        </div>
                    </label>
                    
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="radio"
                            name="exportFormat"
                            value="pdf"
                            checked={exportFormat === 'pdf'}
                            onChange={(e) => setExportFormat(e.target.value)}
                            className="text-blue-600"
                        />
                        <div className="flex items-center gap-2">
                            <FileText size={20} className="text-red-600" />
                            <span>PDF Document (.pdf)</span>
                        </div>
                    </label>
                    
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="radio"
                            name="exportFormat"
                            value="text"
                            checked={exportFormat === 'text'}
                            onChange={(e) => setExportFormat(e.target.value)}
                            className="text-blue-600"
                        />
                        <div className="flex items-center gap-2">
                            <FileText size={20} className="text-blue-600" />
                            <span>Text Document (.txt)</span>
                        </div>
                    </label>
                </div>
                
                <div className="flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleExport}
                        disabled={isExporting}
                        className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 disabled:opacity-50"
                    >
                        {isExporting ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Exporting...
                            </>
                        ) : (
                            <>
                                <Download size={16} />
                                Generate Data
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AnalyticsDashboard; 